/**
 * Authentication Service for ZyloBill Web App
 * 
 * ⚠️ SECURITY WARNING: This MVP implementation stores CouchDB credentials in sessionStorage.
 * This is NOT secure for production use. See tasks.md "Security Notes" section for details.
 * 
 * Acceptable for: MVP, proof-of-concept, internal testing, development
 * NOT acceptable for: Production deployment, public-facing apps, sensitive data
 * 
 * Post-MVP: Implement JWT tokens or backend proxy for production security.
 */

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3000';

class AuthService {
  /**
   * Login user with email and password
   * @param {string} email - User email
   * @param {string} password - User password
   * @returns {Promise<Object>} Login result with user data and tokens
   */
  async login(email, password) {
    try {
      // Generate device ID (browser fingerprint)
      const deviceId = this.getDeviceId();
      const deviceName = this.getDeviceName();
      
      // Call backend login API
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email,
          password,
          deviceId,
          deviceName,
          platform: 'web'
        }),
      });
      
      const data = await response.json();
      
      if (!response.ok || !data.success) {
        throw new Error(data.error || 'Login failed');
      }
      
      // Store session token
      sessionStorage.setItem('sessionToken', data.data.token);
      sessionStorage.setItem('refreshToken', data.data.refreshToken);
      sessionStorage.setItem('deviceToken', data.data.deviceToken);
      sessionStorage.setItem('user', JSON.stringify(data.data.user));
      
      // Get CouchDB credentials
      const couchdbCreds = await this.getCouchDBCredentials(data.data.token, data.data.deviceToken);
      
      // ⚠️ SECURITY WARNING: Storing credentials in sessionStorage (MVP only)
      sessionStorage.setItem('couchdbUrl', couchdbCreds.databaseUrl);
      sessionStorage.setItem('couchdbUsername', couchdbCreds.username);
      sessionStorage.setItem('couchdbPassword', couchdbCreds.password);
      
      console.log('[AuthService] Login successful, CouchDB credentials stored');
      
      // Initialize PouchDB sync (will be implemented in Task 4)
      // For now, just log that credentials are ready
      console.log('[AuthService] CouchDB credentials ready for sync initialization');
      
      return {
        user: data.data.user,
        sessionToken: data.data.token,
        couchdbUrl: couchdbCreds.databaseUrl,
        couchdbUsername: couchdbCreds.username,
        couchdbPassword: couchdbCreds.password,
      };
    } catch (error) {
      console.error('[AuthService] Login error:', error);
      throw error;
    }
  }
  
  /**
   * Get CouchDB credentials from backend
   * @param {string} sessionToken - Session JWT token
   * @param {string} deviceToken - Device token from login
   * @returns {Promise<Object>} CouchDB credentials
   */
  async getCouchDBCredentials(sessionToken, deviceToken) {
    try {
      console.log('[AuthService] Fetching CouchDB credentials from backend...');
      
      const response = await fetch(`${API_URL}/api/couchdb/credentials`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'X-Device-Token': deviceToken,
          'Content-Type': 'application/json',
        },
      });
      
      const data = await response.json();
      
      if (!response.ok || !data.success) {
        console.error('[AuthService] Failed to get CouchDB credentials:', data);
        throw new Error(data.error || 'Failed to get CouchDB credentials');
      }
      
      console.log('[AuthService] CouchDB credentials received:', {
        username: data.data.username,
        databaseUrl: data.data.databaseUrl,
        hasPassword: !!data.data.password
      });
      
      return data.data;
    } catch (error) {
      console.error('[AuthService] Failed to get CouchDB credentials:', error);
      throw error;
    }
  }
  
  /**
   * Logout user and clear all stored credentials
   */
  async logout() {
    try {
      const sessionToken = sessionStorage.getItem('sessionToken');
      
      // Call backend logout API (optional - backend may not have this endpoint)
      if (sessionToken) {
        try {
          await fetch(`${API_URL}/api/auth/logout`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${sessionToken}`,
              'Content-Type': 'application/json',
            },
          });
        } catch (error) {
          console.warn('[AuthService] Backend logout failed (non-critical):', error);
        }
      }
      
      // Clear all stored credentials
      sessionStorage.clear();
      
      console.log('[AuthService] Logout successful, credentials cleared');
    } catch (error) {
      console.error('[AuthService] Logout error:', error);
      throw error;
    }
  }
  
  /**
   * Check if user is authenticated
   * @returns {boolean} True if user has valid session
   */
  isAuthenticated() {
    const sessionToken = sessionStorage.getItem('sessionToken');
    const couchdbUsername = sessionStorage.getItem('couchdbUsername');
    return !!(sessionToken && couchdbUsername);
  }
  
  /**
   * Get current user from session storage
   * @returns {Object|null} User object or null
   */
  getCurrentUser() {
    const userJson = sessionStorage.getItem('user');
    return userJson ? JSON.parse(userJson) : null;
  }
  
  /**
   * Get CouchDB credentials from session storage
   * @returns {Object|null} CouchDB credentials or null
   */
  getCouchDBCredentialsFromStorage() {
    const couchdbUrl = sessionStorage.getItem('couchdbUrl');
    const couchdbUsername = sessionStorage.getItem('couchdbUsername');
    const couchdbPassword = sessionStorage.getItem('couchdbPassword');
    
    if (!couchdbUrl || !couchdbUsername || !couchdbPassword) {
      return null;
    }
    
    return {
      databaseUrl: couchdbUrl,
      username: couchdbUsername,
      password: couchdbPassword,
    };
  }
  
  /**
   * Get or generate device ID (browser fingerprint)
   * @returns {string} Device ID
   */
  getDeviceId() {
    let deviceId = localStorage.getItem('deviceId');
    if (!deviceId) {
      // Generate simple device ID based on browser info
      deviceId = `web_${this.generateUUID()}`;
      localStorage.setItem('deviceId', deviceId);
    }
    return deviceId;
  }
  
  /**
   * Get device name (browser + OS)
   * @returns {string} Device name
   */
  getDeviceName() {
    const ua = navigator.userAgent;
    const browser = this.getBrowserName(ua);
    const os = this.getOSName(ua);
    return `${browser} on ${os}`;
  }
  
  /**
   * Get browser name from user agent
   * @param {string} ua - User agent string
   * @returns {string} Browser name
   */
  getBrowserName(ua) {
    if (ua.includes('Chrome') && !ua.includes('Edg')) return 'Chrome';
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari';
    if (ua.includes('Edg')) return 'Edge';
    return 'Unknown Browser';
  }
  
  /**
   * Get OS name from user agent
   * @param {string} ua - User agent string
   * @returns {string} OS name
   */
  getOSName(ua) {
    if (ua.includes('Windows')) return 'Windows';
    if (ua.includes('Mac')) return 'macOS';
    if (ua.includes('Linux')) return 'Linux';
    if (ua.includes('Android')) return 'Android';
    if (ua.includes('iOS') || ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
    return 'Unknown OS';
  }
  
  /**
   * Generate a simple UUID v4
   * @returns {string} UUID
   */
  generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
      const r = Math.random() * 16 | 0;
      const v = c === 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }
}

// Export singleton instance
export default new AuthService();
